import { useState, useEffect, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import { Flame, Clock, Users, Leaf, Heart, ChefHat, Play, Pause, RotateCcw, ArrowLeft, ArrowRight, X, Wand2, Loader2, Sparkles, Share2 } from "lucide-react";

const TWEAK_PRESETS = [
  "Make it vegan",
  "Make it gluten-free",
  "Halve the calories",
  "Double the protein",
  "Make it dairy-free",
  "Scale to 4 servings",
];

function CookingMode({ recipe, onExit }) {
  const [step, setStep] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const timer = useRef(null);
  const steps = recipe.instructions || [];

  useEffect(() => {
    if (running) {
      timer.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    }
    return () => clearInterval(timer.current);
  }, [running]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[60] bg-[#2C221E] text-white flex flex-col" data-testid="cooking-mode">
      <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
        <div className="flex items-center gap-2 text-[#D49B4B]">
          <ChefHat className="w-5 h-5" />
          <span className="font-mono text-xs uppercase tracking-widest">Cooking Mode</span>
        </div>
        <button onClick={onExit} data-testid="exit-cooking-btn" className="p-2 rounded-lg hover:bg-white/10"><X className="w-5 h-5" /></button>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center max-w-3xl mx-auto">
        <span className="font-mono text-sm text-[#C5B8A5]">Step {step + 1} of {steps.length}</span>
        <p className="font-serif text-3xl md:text-5xl leading-tight mt-6">{steps[step]}</p>
      </div>

      <div className="px-6 py-6 border-t border-white/10">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="font-serif text-3xl tabular-nums">{mm}:{ss}</div>
            <button onClick={() => setRunning((r) => !r)} className="p-2.5 rounded-full bg-[#C85A32] hover:bg-[#B04B26]" data-testid="timer-toggle">
              {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            </button>
            <button onClick={() => { setSeconds(0); setRunning(false); }} className="p-2.5 rounded-full bg-white/10 hover:bg-white/20">
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              disabled={step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              className="h-11 w-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center disabled:opacity-30"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button
              disabled={step >= steps.length - 1}
              onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
              data-testid="next-step-btn"
              className="h-11 px-6 rounded-full bg-[#4A6B4E] hover:bg-[#3B563E] flex items-center gap-2 disabled:opacity-40"
            >
              Next <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function RecipeModal({ recipe: initialRecipe, open, onClose, onUpdate }) {
  const [cooking, setCooking] = useState(false);
  const [current, setCurrent] = useState(initialRecipe);
  const [tweaking, setTweaking] = useState("");
  const [customTweak, setCustomTweak] = useState("");
  useEffect(() => { setCurrent(initialRecipe); }, [initialRecipe]);
  if (!initialRecipe) return null;
  const recipe = current || initialRecipe;
  const n = recipe.nutrition || {};

  const handleShare = async () => {
    const ingredientLines = (recipe.ingredients || []).map((ing) =>
      [ing.quantity, ing.unit, ing.name].filter(Boolean).join(" ")
    );
    const ingredientsSection = ingredientLines.length
      ? "\n\nIngredients:\n" + ingredientLines.join("\n")
      : "";
    const stepsSection =
      "\n\nSteps:\n" +
      (recipe.instructions || []).map((step, i) => `${i + 1}. ${step}`).join("\n");
    const text = recipe.title + ingredientsSection + stepsSection;

    const copyFallback = async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Recipe copied — paste it anywhere to share!");
      } catch {
        toast.error("Could not copy the recipe.");
      }
    };

    if (navigator.share) {
      try {
        await navigator.share({ title: recipe.title, text });
      } catch (err) {
        if (err?.name !== "AbortError") await copyFallback();
      }
    } else {
      await copyFallback();
    }
  };

  const applyTweak = async (instruction) => {
    if (!instruction.trim() || tweaking) return;
    setTweaking(instruction);
    try {
      const res = await api.post("/recipes/tweak", { recipe, instruction });
      setCurrent(res.data.recipe);
      onUpdate && onUpdate(res.data.recipe);
      setCustomTweak("");
      toast.success("Recipe reinvented by Claude");
    } catch (e) {
      if (e?.response?.status === 402) toast.error("Your free month ended — subscribe to keep tweaking");
      else if (e?.response?.status === 429) toast.error("Daily AI limit reached. Try again tomorrow.");
      else toast.error("Couldn't tweak that recipe. Try again.");
    } finally {
      setTweaking("");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-thin bg-[#FBF9F5] border-[#E2D9CC] rounded-2xl p-0" data-testid="recipe-modal">
          {recipe.image_url && (
            <div className="h-56 w-full overflow-hidden rounded-t-2xl">
              <img src={recipe.image_url} alt={recipe.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6 md:p-8">
            <DialogHeader>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {(recipe.dietary_tags || []).map((t) => (
                  <span key={t} className="px-2.5 py-1 rounded-full bg-[#E8F0E6] text-[11px] font-mono uppercase tracking-wider text-[#4A6B4E]">{t}</span>
                ))}
              </div>
              <DialogTitle className="font-serif text-3xl text-left tracking-tight">{recipe.title}</DialogTitle>
        <button data-testid="recipe-share-btn" onClick={handleShare} className="inline-flex items-center gap-2 rounded-xl border border-[#E8DDD0] px-3 py-2 text-sm font-medium text-[#2C221E] hover:bg-[#FAF7F2]">
          <Share2 className="w-4 h-4" />
          Share
        </button>
            </DialogHeader>
            <p className="text-[#6E6259] mt-2">{recipe.description}</p>

            <div className="flex flex-wrap items-center gap-5 mt-5 text-sm text-[#6E6259]">
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#C85A32]" />{recipe.prep_time || 0}m prep · {recipe.cook_time || 0}m cook</span>
              <span className="flex items-center gap-1.5"><Users className="w-4 h-4 text-[#C85A32]" />{recipe.servings || 2} servings</span>
            </div>

            {/* Nutrition */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[["Calories", n.calories, "kcal", "#C85A32", Flame], ["Protein", n.protein, "g", "#4A6B4E", null], ["Carbs", n.carbs, "g", "#D49B4B", null], ["Fat", n.fat, "g", "#8FA885", null]].map(([l, v, u, c, Icon]) => (
                <div key={l} className="rounded-xl bg-white border border-[#E2D9CC] p-4 text-center">
                  <div className="font-serif text-2xl" style={{ color: c }}>{v ?? "–"}<span className="text-sm">{u}</span></div>
                  <div className="text-xs text-[#9C8F85] uppercase tracking-wide mt-0.5">{l}</div>
                </div>
              ))}
            </div>

            {recipe.health_note && (
              <div className="flex items-start gap-2 mt-5 rounded-xl bg-[#E8F0E6] p-4 text-sm text-[#3B563E]">
                <Heart className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{recipe.health_note}</span>
              </div>
            )}
            {recipe.zero_waste_tip && (
              <div className="flex items-start gap-2 mt-3 rounded-xl bg-[#FDF5E6] p-4 text-sm text-[#8a6d2a]">
                <Leaf className="w-4 h-4 mt-0.5 shrink-0" />
                <span><b>Zero-waste:</b> {recipe.zero_waste_tip}</span>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-8 mt-8">
              <div>
                <h4 className="font-serif text-xl mb-3">Ingredients</h4>
                <ul className="space-y-2">
                  {(recipe.ingredients || []).map((ing, i) => (
                    <li key={`${ing.name || "ing"}-${i}`} className="flex items-center gap-2 text-sm">
                      <span className={`w-2 h-2 rounded-full ${ing.have ? "bg-[#4A6B4E]" : "bg-[#C85A32]"}`} />
                      <span className="text-[#2C221E]">{[ing.quantity, ing.unit, ing.name].filter(Boolean).join(" ")}</span>
                      {!ing.have && <span className="text-[10px] text-[#C85A32] font-mono uppercase">buy</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-serif text-xl mb-3">Method</h4>
                <ol className="space-y-3">
                  {(recipe.instructions || []).map((s, i) => (
                    <li key={`step-${i}`} className="flex gap-3 text-sm text-[#6E6259]">
                      <span className="w-6 h-6 rounded-full bg-[#C85A32] text-white flex items-center justify-center text-xs shrink-0 font-mono">{i + 1}</span>
                      <span className="leading-relaxed pt-0.5">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            {/* AI tweak panel */}
            <div className="mt-8 rounded-2xl border border-[#E2D9CC] bg-white/70 p-5" data-testid="tweak-panel">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#2C221E] flex items-center justify-center">
                  <Wand2 className="w-4 h-4 text-[#D49B4B]" />
                </span>
                <div>
                  <h4 className="font-serif text-lg leading-none">Tweak with AI</h4>
                  <p className="text-xs text-[#9C8F85] mt-1">Claude rewrites the recipe & recomputes nutrition instantly.</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {TWEAK_PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`tweak-preset-${p.replace(/\s+/g, "-").toLowerCase()}`}
                    disabled={!!tweaking}
                    onClick={() => applyTweak(p)}
                    className="rounded-full border border-[#E2D9CC] bg-white px-3.5 py-1.5 text-sm text-[#2C221E] hover:border-[#C85A32] hover:text-[#C85A32] transition-colors disabled:opacity-40 flex items-center gap-1.5"
                  >
                    {tweaking === p ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-[#D49B4B]" />}
                    {p}
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); applyTweak(customTweak); }} className="flex gap-2 mt-3">
                <Input
                  data-testid="tweak-custom-input"
                  value={customTweak}
                  onChange={(e) => setCustomTweak(e.target.value)}
                  placeholder="Or ask for anything… e.g. 'make it kid-friendly and spicier'"
                  disabled={!!tweaking}
                  className="rounded-xl bg-white border-[#E2D9CC] h-11 focus-visible:ring-[#C85A32]"
                />
                <Button
                  type="submit"
                  data-testid="tweak-apply-btn"
                  disabled={!!tweaking || !customTweak.trim()}
                  className="rounded-xl bg-[#2C221E] hover:bg-[#3d2f28] text-white h-11 px-4 shrink-0"
                >
                  {tweaking && tweaking === customTweak ? <Loader2 className="w-5 h-5 animate-spin" /> : <Wand2 className="w-5 h-5" />}
                </Button>
              </form>
            </div>

            <Button
              data-testid="start-cooking-btn"
              onClick={() => setCooking(true)}
              className="w-full mt-4 h-12 rounded-xl bg-[#C85A32] hover:bg-[#B04B26] text-white text-base"
            >
              <ChefHat className="w-5 h-5 mr-2" /> Start hands-free cooking
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {cooking && <CookingMode recipe={recipe} onExit={() => setCooking(false)} />}
    </>
  );
}
